import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";


import {
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan,
  getProductos,
  getProductoEstado,
  getPlanes,
  diagnosePlanExists,
  testDirectQuery,
  testSupabaseConnection
} from "../../services/divinaPlanesService";
import { getPolizasByPlanId } from "../../services/divinaPolizasService";
import { updatePreciosBase, getPreciosBase as getPreciosBaseSupabase, getPreciosCarga, updatePreciosCarga, updateCoberturaJson } from "../../services/supabase/planesSupabaseService";
import { toast } from "sonner";
import { getErrorSummary, logError } from "../../lib/utils";
import { validarPlanUrl, urlParaGuardar } from "../../lib/planUrl";
import FileUpload from "../../components/ui/FileUpload";
import ConfiguracionFinancieraTab from "../../components/tabs/ConfiguracionFinancieraTab";
import ConfiguracionJsonTab from "../../components/tabs/ConfiguracionJsonTab";
import ConfiguracionPlanTab from "../../components/tabs/ConfiguracionPlanTab";
import PreciosBaseTab from "../../components/tabs/PreciosBaseTab";
import DescuentosBaseTab from "../../components/tabs/DescuentosBaseTab";
import CoberturasTab from "../../components/tabs/CoberturasTab";
import JsonRawTab from "../../components/tabs/JsonRawTab";
import BackConfigEditor from "../wallet/BackConfigEditor";
import CalculatorConfigTab from "../../components/tabs/CalculatorConfigTab";
import SugerenciasTab from "../../components/tabs/SugerenciasTab";
import { defaultJsonConfig, parseDescripcionJson, buildDescripcionJson } from "./descripcionJsonRoundtrip";
import {
  FK, PageHead, FormCard, Section, Row, Field, TextInput, TextArea, Select,
  SideNav, DocsMeter, ActionBar, InfoToggleRow, FkRadioGroup, FkPrimary, FkDanger, YIcon,
} from "../../components/product_config/formkit";

// Verificación de variables de entorno en el componente
console.log('🔍 DivinaPlanesForm - Verificando variables de entorno:');
console.log('📍 REACT_APP_SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL ? 'Definida' : 'NO DEFINIDA');
console.log('🔑 REACT_APP_SUPABASE_KEY:', process.env.REACT_APP_SUPABASE_KEY ? 'Definida' : 'NO DEFINIDA');
console.log('🌍 Variables REACT_APP disponibles:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));

// Función para probar conexión de Supabase
const testConnectionOnLoad = async () => {
  try {
    console.log('🧪 DivinaPlanesForm - Probando conexión a Supabase...');
    const result = await testSupabaseConnection();

    if (result.success) {
      console.log('✅ DivinaPlanesForm - Conexión a Supabase exitosa:', result);
    } else {
      logError('❌ DivinaPlanesForm - Error en conexión a Supabase:', result, { ignoreAbort: true });
      toast.error(`Error de conexión: ${result.error}`);
    }

    return result;
  } catch (error) {
    logError('❌ DivinaPlanesForm - Error inesperado al probar conexión:', error, { ignoreAbort: true });
    toast.error('Error inesperado al conectar con la base de datos');
    return { success: false, error: getErrorSummary(error).message };
  }
};

// Ejecutar prueba de conexión al cargar el componente
testConnectionOnLoad();

const normalizeJsonValue = (value) => {
  if (value == null || value === "") return null;

  let current = value;

  for (let i = 0; i < 3; i += 1) {
    if (typeof current !== "string") return current;

    const trimmed = current.trim();
    if (!trimmed) return null;

    try {
      current = JSON.parse(trimmed);
    } catch {
      return current;
    }
  }

  return current;
};

const toJsonEditorValue = (value) => {
  const normalized = normalizeJsonValue(value);

  if (normalized == null || normalized === "") return "";
  if (typeof normalized === "string") return normalized;

  return JSON.stringify(normalized, null, 2);
};

/* Secciones del formulario de plan (rail vertical estilo "settings").
   value = coincide con el activeTab original para no romper el estado. */
const PLAN_TABS = [
  { value: "informacion-basica", label: "Información básica", icon: "clipboard" },
  { value: "configuracion-financiera", label: "Configuración financiera", icon: "wallet" },
  { value: "configuracion-json", label: "Configuración JSON", icon: "layers" },
  { value: "configuracion-plan", label: "Configuración plan", icon: "settings" },
  { value: "calculator-config", label: "Calculator config", icon: "calculator" },
  { value: "sugerencias", label: "Sugerencias", icon: "info" },
  { value: "precios-base", label: "Precios base (UF)", icon: "barChart" },
  { value: "descuentos-base", label: "Descuentos base", icon: "percent" },
  { value: "coberturas", label: "Coberturas", icon: "shield" },
  { value: "wallet-reverso", label: "Reverso del pase", icon: "wallet" },
  { value: "json-raw", label: "JSON raw", icon: "document" },
];
const TAB_META = {
  "informacion-basica": { title: "Información básica", desc: "Identificación del plan, documentos obligatorios y visibilidad." },
  "configuracion-financiera": { title: "Configuración financiera", desc: "Parámetros económicos base del plan." },
  "configuracion-json": { title: "Configuración JSON", desc: "Estructura del plan que arma el campo jsonarray del flujo y el correo." },
  "configuracion-plan": { title: "Configuración plan", desc: "Ajustes específicos del plan." },
  "calculator-config": { title: "Calculator config", desc: "Modelo de cálculo de la prima: tramos por edad, cargas y descuentos." },
  "sugerencias": { title: "Sugerencias / Cross-sell", desc: "Participación de este plan en las sugerencias de otros flujos." },
  "precios-base": { title: "Precios base (UF)", desc: "Tiers de precio que se materializan en las cards del flujo y el correo." },
  "descuentos-base": { title: "Descuentos base", desc: "Descuentos que se listan en la cotización." },
  "coberturas": { title: "Coberturas", desc: "Edita json_coberturas — cada cobertura y su valor." },
  "wallet-reverso": { title: "Reverso del pase", desc: "Contactos y enlaces que salen al dorso del pase de Apple/Google Wallet. Se mezclan con los de la compañía y los de la póliza colectiva." },
  "json-raw": { title: "JSON raw", desc: "Edición avanzada de los campos JSON del registro, con validación en vivo." },
};

export default function DivinaPlanesForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Form data based on SQL schema
  const [formData, setFormData] = useState({
    nombre: '',
    codigo_plan: '',
    producto_id: '',
    descripcion: '',
    condicionado_archivo_id: null,
    cuadro_coberturas_archivo_id: null,
    certcondicionado_archivo_id: null,
    descripcion_json: "",
    jsonarray: "",
    jsonmails: "",
    siniestros_json: "",
    uf_base: "",
    reglas_deducible: "",
    activo: true,
    cobertura: "",
    capital_uf: "",
    prevision: "",
    es_complementario: false,
    planes_base: [],
    json_coberturas: "",
    solo_empresa: false,
    image_url: "",
    is_url: false,
    url: "",
    tipo_emision: "individual",
    moneda: "UF",
    subscription: true,
    contado: true
  });

  // Structured JSON configuration
  const [jsonConfig, setJsonConfig] = useState(defaultJsonConfig);

  // Related data
  const [productos, setProductos] = useState([]);
  const [, setActividades] = useState([]);
  const [polizas, setPolizas] = useState([]);

  // Precios base state
  const [preciosBase, setPreciosBase] = useState([]);
  const [preciosCarga, setPreciosCarga] = useState([]);
  const [newPrecio, setNewPrecio] = useState({
    tramo_etario: "",
    sexo: "",
    actividad_id: "",
    capital_id: "",
    deducible_id: "",
    precio_uf: "",
    prevision: "",
    edad_minima: "",
    edad_maxima: "",
    activo: true,
    Activo: true,
    tasa: "",
    monto_minimo: "",
    monto_maximo: "",
    moneda: "UF",
    frecuencia: "mensual",
    n_cuotas: "",
    poliza_colectiva_id: null
  });

  // Descuentos base state
  const [descuentosBase, setDescuentosBase] = useState([]);
  const [newDescuento, setNewDescuento] = useState({
    cargas_min: "",
    cargas_max: "",
    porcentaje: "",
    acumulable: false,
    tipo_carga: ""
  });

  // Snapshot for dirty-check
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const hasChanges = !isEditing || initialSnapshot === null || (
    JSON.stringify(formData) !== JSON.stringify(initialSnapshot.formData) ||
    JSON.stringify(preciosBase) !== JSON.stringify(initialSnapshot.preciosBase) ||
    JSON.stringify(preciosCarga) !== JSON.stringify(initialSnapshot.preciosCarga)
  );

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [showPreciosForm, setShowPreciosForm] = useState(false);
  const [showDescuentosForm, setShowDescuentosForm] = useState(false);
  const [activeTab, setActiveTab] = useState("informacion-basica");
  const [planes, setPlanes] = useState([]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load plan data if editing
  useEffect(() => {
    if (id) {
      setPlanLoaded(false);
      setInitialSnapshot(null);
      loadPlanData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Parse JSON when formData.descripcion_json changes
  useEffect(() => {
    if (formData.descripcion_json && id) {
      parseExistingJson(formData.descripcion_json);
    }
  }, [formData.descripcion_json, id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const productosData = await getProductos();
      const planesData = await getPlanes();

      setProductos(productosData || []);
      setPlanes(planesData || []);
      // TODO: Implementar getActividades cuando esté disponible
      setActividades([]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Error al cargar datos iniciales');
    } finally {
      setLoading(false);
    }
  };

  const loadPlanData = async () => {
    try {
      setLoading(true);
      const planData = await getPlanById(id);
      setFormData({
        nombre: planData.nombre || "",
        descripcion: planData.descripcion || "",
        descripcion_json: toJsonEditorValue(planData.descripcion_json),
        jsonarray: toJsonEditorValue(planData.jsonarray),
        jsonmails: toJsonEditorValue(planData.jsonmails),
        siniestros_json: toJsonEditorValue(planData.siniestros_json),
        uf_base: planData.uf_base || "",
        reglas_deducible: planData.reglas_deducible || "",
        activo: planData.activo ?? true,
        cobertura: planData.cobertura || "",
        capital_uf: planData.capital_uf || "",
        prevision: planData.prevision || "",
        codigo_plan: planData.codigo_plan || "",
        producto_id: planData.producto_id || "",
        condicionado_archivo_id: planData.condicionado_archivo_id || null,
        cuadro_coberturas_archivo_id: planData.cuadro_coberturas_archivo_id || null,
        certcondicionado_archivo_id: planData.certcondicionado_archivo_id || null,
        es_complementario: planData.es_complementario || false,
        planes_base: planData.planes_base || [],
        json_coberturas: planData.json_coberturas ? JSON.stringify(planData.json_coberturas) : "",
        solo_empresa: planData.solo_empresa || false,
        image_url: planData.image_url || "",
        is_url: planData.is_url || false,
        url: planData.url || "",
        tipo_emision: planData.tipo_emision || "individual",
        moneda: planData.moneda || "UF",
        subscription: planData.subscription ?? true,
        contado: planData.contado ?? true
      });

      // Load related precios and descuentos
      console.log('🔍 Cargando precios base desde la base de datos...');
      const preciosBaseData = await getPreciosBaseSupabase(id);
      setPreciosBase(preciosBaseData || []);

      const preciosCargaData = await getPreciosCarga(id);
      setPreciosCarga(preciosCargaData || []);

      // Cargar polizas colectivas del plan
      const polizasData = await getPolizasByPlanId(id);
      setPolizas(polizasData || []);

      if (planData.descuentos) {
        setDescuentosBase(planData.descuentos);
      }

      setPlanLoaded(true);
    } catch (error) {
      console.error('Error loading plan:', error);
      toast.error('Error al cargar el plan');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle JSON config changes
  const handleJsonConfigChange = (path, value) => {
    // Handle direct formData changes for jsonarray
    if (path === 'jsonarray') {
      setFormData(prev => ({
        ...prev,
        jsonarray: value
      }));
      return;
    }

    setJsonConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  // Sync JSON config with descripcion_json
  useEffect(() => {
    // `preciosBase` entra porque de ahí sale `frecuenciaPrima`: es el espejo de
    // precios_base.frecuencia y se recalcula cuando cambia el programa de cobro.
    const descripcionJson = buildDescripcionJson(jsonConfig, preciosBase);

    const jsonString = JSON.stringify(descripcionJson, null, 2);
    setFormData(prev => {
      const next = { ...prev, descripcion_json: jsonString };
      if (planLoaded && initialSnapshot === null) {
        setInitialSnapshot({ formData: next, preciosBase, preciosCarga });
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonConfig, planLoaded, preciosBase]);

  // Parse existing JSON when loading plan data
  const parseExistingJson = (jsonString) => {
    try {
      if (jsonString) {
        setJsonConfig(parseDescripcionJson(jsonString));
      }
    } catch (error) {
      console.error('Error parsing JSON:', error);
    }
  };

  const handleAddPrecio = () => {
    // monto_tasa tarifica con `tasa` (no precio_uf); el resto requiere precio_uf.
    if (jsonConfig?.calculatorConfig?.type === "monto_tasa") {
      if (!newPrecio.tasa) {
        toast.error('La tasa es requerida');
        return;
      }
    } else if (!newPrecio.precio_uf) {
      toast.error('El precio en UF es requerido');
      return;
    }

    // Un precio mensual sin n_cuotas lo rechaza la DB
    // (precios_base_n_cuotas_requerido_check). Se avisa aquí para no dejar que
    // el error aparezca recién al guardar, como una falla cruda de Postgres.
    if ((newPrecio.frecuencia || 'mensual') !== 'anual' && !newPrecio.n_cuotas) {
      toast.error('El N° de cuotas es requerido en un precio mensual');
      return;
    }

    setPreciosBase(prev => [...prev, { ...newPrecio, id: Date.now() }]);
    setNewPrecio({
      tramo_etario: "",
      sexo: "",
      actividad_id: "",
      capital_id: "",
      deducible_id: "",
      precio_uf: "",
      prevision: "",
      edad_minima: "",
      edad_maxima: "",
      Activo: true,
      tasa: "",
      monto_minimo: "",
      monto_maximo: "",
      moneda: "UF",
      frecuencia: "mensual",
      n_cuotas: ""
    });
    setShowPreciosForm(false);
  };

  const handleRemovePrecio = (index) => {
    setPreciosBase(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddDescuento = () => {
    if (!newDescuento.porcentaje) {
      toast.error('El porcentaje es requerido');
      return;
    }

    setDescuentosBase(prev => [...prev, { ...newDescuento, id: Date.now() }]);
    setNewDescuento({
      cargas_min: "",
      cargas_max: "",
      porcentaje: "",
      acumulable: false,
      tipo_carga: ""
    });
    setShowDescuentosForm(false);
  };

  const handleRemoveDescuento = (index) => {
    setDescuentosBase(prev => prev.filter((_, i) => i !== index));
  };

  const handleDescuentoAplicacionChange = (value) => {
    setJsonConfig(prev => ({
      ...prev,
      calculatorConfig: {
        ...prev?.calculatorConfig,
        descuentosCargas: {
          ...prev?.calculatorConfig?.descuentosCargas,
          aplicacion: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // VERIFICACIÓN DE VARIABLES DE ENTORNO AL INICIO
    console.log('🔍 HANDLESUBMIT - Verificando variables de entorno:');
    console.log('📍 REACT_APP_SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL ? 'Definida' : 'NO DEFINIDA');
    console.log('🔑 REACT_APP_SUPABASE_KEY:', process.env.REACT_APP_SUPABASE_KEY ? 'Definida' : 'NO DEFINIDA');
    console.log('🌍 process.env en handleSubmit:', process.env);
    console.log('📊 Valores reales:');
    console.log('  - URL:', process.env.REACT_APP_SUPABASE_URL);
    console.log('  - KEY (primeros 20):', process.env.REACT_APP_SUPABASE_KEY ? process.env.REACT_APP_SUPABASE_KEY.substring(0, 20) + '...' : 'undefined');

    // Verificar si las variables están undefined
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_KEY) {
      console.error('❌ ERROR CRÍTICO: Variables de entorno de Supabase no están definidas');
      toast.error('Error de configuración: Variables de entorno de Supabase no disponibles');
      return;
    }

    // PRUEBA DE CONEXIÓN ANTES DE PROCEDER
    console.log('🧪 HANDLESUBMIT - Probando conexión a Supabase antes de proceder...');
    const connectionTest = await testSupabaseConnection();

    if (!connectionTest.success) {
      console.error('❌ ERROR CRÍTICO: No se puede conectar a Supabase:', connectionTest);
      toast.error(`Error de conexión a Supabase: ${connectionTest.error}`);
      return;
    }

    console.log('✅ HANDLESUBMIT - Conexión a Supabase confirmada, procediendo...');

    // DEBUG: Log del ID del plan al inicio
    console.log('=== DEBUG INICIO HANDLESUBMIT ===');
    console.log('ID del plan:', id);
    console.log('isEditing:', isEditing);
    console.log('Tipo de ID:', typeof id);
    console.log('ID es undefined?:', id === undefined);
    console.log('ID es null?:', id === null);

    if (!formData.nombre.trim()) {
      toast.error('El nombre del plan es requerido');
      return;
    }

    if (!formData.producto_id) {
      toast.error('Debe seleccionar un producto');
      return;
    }

    // Idem handleAddPrecio, pero sobre las filas ya cargadas: la DB rechaza el
    // upsert completo si alguna viola el constraint, así que se avisa cuáles
    // son antes de intentar guardar. Solo aplica a precios activos mensuales:
    // los inactivos son histórico y una prima anual es 1 cobro por definición.
    const preciosSinCuotas = preciosBase.filter(
      (p) => (p?.Activo ?? true) && (p?.frecuencia || 'mensual') !== 'anual' && !p?.n_cuotas
    );
    if (preciosSinCuotas.length > 0) {
      toast.error(
        `${preciosSinCuotas.length} precio(s) activo(s) sin N° de cuotas. Complétalos en la pestaña "Precios Base" antes de guardar.`
      );
      setActiveTab('precios-base');
      return;
    }

    // No se puede guardar un plan como activo si su producto está inactivo:
    // sería un estado inconsistente. Se consulta el estado real del producto
    // (getProductoEstado trae el producto aunque no esté activo, a diferencia
    // del selector que solo lista los activos). Solo bloquea al activar; guardar
    // un plan inactivo se permite siempre.
    if (formData.activo) {
      try {
        const prod = await getProductoEstado(formData.producto_id);
        if (prod && prod.activo === false) {
          toast.error(
            `No se puede activar este plan: el producto "${prod.nombre || 'asociado'}" está inactivo. Actívalo primero o guarda el plan como inactivo.`
          );
          return;
        }
      } catch (err) {
        // Si la verificación falla (red/DB), no bloqueamos el guardado por eso:
        // se registra y se continúa (la regla se re-aplica en la lista).
        logError('Verificación de producto activo falló', err);
      }
    }

    // Validación del ID antes de actualizar
    if (isEditing && (!id || id === 'undefined' || id === 'null')) {
      console.error('ERROR: ID del plan no válido para actualización:', id);
      toast.error('Error: ID del plan no válido');
      return;
    }

    try {
      setSaving(true);

      // Debug: Log current jsonConfig and formData
      console.log('=== DEBUG GUARDADO PLAN ===');
      console.log('jsonConfig actual:', jsonConfig);
      console.log('formData.descripcion_json:', formData.descripcion_json);
      console.log('modulos_requeridos en jsonConfig:', jsonConfig.modulos_requeridos);

      // Parse JSON if provided
      let descripcionJson = null;
      if (formData.descripcion_json.trim()) {
        try {
          descripcionJson = JSON.parse(formData.descripcion_json);
          console.log('JSON parseado:', descripcionJson);
          console.log('modulos_requeridos en JSON parseado:', descripcionJson.modulos_requeridos);
        } catch (error) {
          console.error('Error parsing JSON:', error);
          toast.error('El JSON de descripción no es válido');
          return;
        }
      }

      let jsonArrayData = null;
      if (formData.jsonarray.trim()) {
        try {
          jsonArrayData = JSON.parse(formData.jsonarray);
          console.log('JSON Array parseado:', jsonArrayData);
        } catch (error) {
          console.error('Error parsing JSON Array:', error);
          toast.error('El JSON Array no es válido');
          return;
        }
      }

      let jsonMailsData = null;
      if (formData.jsonmails.trim()) {
        try {
          jsonMailsData = JSON.parse(formData.jsonmails);
          console.log('JSON Mails parseado:', jsonMailsData);
        } catch (error) {
          console.error('Error parsing JSON Mails:', error);
          toast.error('El JSON Mails no es válido');
          return;
        }
      }

      let jsonSinistrosData = null;
      if (formData.siniestros_json && formData.siniestros_json.trim()) {
        try {
          jsonSinistrosData = JSON.parse(formData.siniestros_json);
          console.log('JSON Sinistros parseado:', jsonSinistrosData);
        } catch (error) {
          console.error('Error parsing JSON Sinistros:', error);
          toast.error('El JSON Sinistros no es válido');
          return;
        }
      }

      let coberturaJsonData = null;
      if (formData.json_coberturas && formData.json_coberturas.trim()) {
        try {
          coberturaJsonData = JSON.parse(formData.json_coberturas);
        } catch (error) {
          console.error('Error parsing json_coberturas:', error);
          toast.error('El JSON de coberturas no es válido');
          return;
        }
      }

      // Un plan tipo URL sin URL válida flow lo DESCARTA en silencio, así que se
      // rechaza acá en vez de publicar un plan que nadie va a ver.
      const errorUrl = validarPlanUrl(formData.is_url, formData.url);
      if (errorUrl) {
        toast.error(errorUrl);
        return;
      }

      const { json_coberturas: _jsonCoberturasRaw, ...planDataBase } = formData;
      const planData = {
        ...planDataBase,
        descripcion_json: descripcionJson,
        jsonarray: jsonArrayData,
        jsonmails: jsonMailsData,
        siniestros_json: jsonSinistrosData,
        uf_base: formData.uf_base ? parseFloat(formData.uf_base) : null,
        capital_uf: formData.capital_uf ? parseFloat(formData.capital_uf) : null,
        prevision: formData.prevision || null,
        url: urlParaGuardar(formData.is_url, formData.url)
      };

      console.log('planData final a enviar:', planData);
      console.log('descripcion_json en planData:', planData.descripcion_json);
      console.log('📌 es_complementario en planData:', planData.es_complementario);
      console.log('📌 planes_base en planData:', planData.planes_base);

      if (isEditing) {
        // DEBUG: Log antes de llamar updatePlan
        console.log('=== DEBUG ANTES DE UPDATEPLAN ===');
        console.log('ID para updatePlan:', id);
        console.log('planData para updatePlan:', planData);
        console.log('Verificando que el ID existe en la base de datos...');

        // DIAGNÓSTICO: Verificar existencia del plan con función específica
        console.log('🔍 DIAGNÓSTICO: Ejecutando diagnosePlanExists...');
        const diagnosticResult = await diagnosePlanExists(id);
        console.log('📊 DIAGNÓSTICO: Resultado completo:', JSON.stringify(diagnosticResult, null, 2));

        // Ejecutar diagnóstico directo adicional
        console.log('\n🔬 Ejecutando diagnóstico directo adicional...');
        const directDiagnostic = await testDirectQuery(id);
        console.log('📋 Resultado del diagnóstico directo:', JSON.stringify(directDiagnostic, null, 2));

        // Log adicional para verificar configuración de Supabase
        console.log('🔧 Configuración de Supabase:', {
          url: process.env.REACT_APP_SUPABASE_URL ? 'CONFIGURADA' : 'NO CONFIGURADA',
          key: process.env.REACT_APP_SUPABASE_KEY ? 'CONFIGURADA' : 'NO CONFIGURADA'
        });

        if (!diagnosticResult.exists) {
          console.error('❌ DIAGNÓSTICO: Plan NO EXISTE en la base de datos');
          toast.error(`Plan con ID ${id} no encontrado en la base de datos`);
        } else {
          console.log('✅ DIAGNÓSTICO: Plan CONFIRMADO que existe');
        }

        let updatePlanFailed = false;
        try {
          const existingPlan = await getPlanById(id);
          console.log('Plan existente encontrado:', JSON.stringify(existingPlan, null, 2));

          console.log('🚀 Procediendo con updatePlan...');

          await updatePlan(id, planData);
        } catch (updateError) {
          updatePlanFailed = true;
          console.error('Error específico en updatePlan:', updateError);
          console.error('Código de error:', updateError.code);
          console.error('Detalles del error:', updateError.details);
          console.error('Mensaje del error:', updateError.message);
        }

        console.log('💰 Guardando precios base:', preciosBase);
        await updatePreciosBase(id, preciosBase);
        await updatePreciosCarga(id, preciosCarga);

        if (coberturaJsonData) {
          console.log('🗂️ Guardando json_coberturas...');
          await updateCoberturaJson(id, coberturaJsonData);
        }

        if (updatePlanFailed) {
          toast.error('Error al actualizar el plan');
        } else {
          toast.success('Plan actualizado exitosamente');
          setInitialSnapshot({ formData: { ...formData }, preciosBase: [...preciosBase], preciosCarga: [...preciosCarga] });
        }
        // No redirigir al actualizar, permanecer en la misma página
      } else {
        const newPlan = await createPlan(planData);
        const newPlanId = newPlan.id;

        // Guardar precios base después de crear el plan
        console.log('💰 Guardando precios base para nuevo plan:', preciosBase);
        await updatePreciosBase(newPlanId, preciosBase);
        await updatePreciosCarga(newPlanId, preciosCarga);

        toast.success('Plan creado exitosamente');
        // Solo redirigir cuando se crea un nuevo plan
        navigate('/product_config/planes');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Error al guardar el plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await deletePlan(id);
      toast.success('Plan eliminado exitosamente');
      navigate('/product_config/planes');
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error al eliminar el plan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const productoOptions = productos.map((p) => ({ value: p.id, label: p.nombre }));
  const docsDone = [formData.condicionado_archivo_id, formData.cuadro_coberturas_archivo_id, formData.certcondicionado_archivo_id].filter(Boolean).length;
  const railOptions = PLAN_TABS.map((t) =>
    t.value === "precios-base" ? { ...t, count: preciosBase.length }
    : t.value === "descuentos-base" ? { ...t, count: descuentosBase.length }
    : t
  );
  const meta = TAB_META[activeTab];

  return (
    <div className="p-6" style={{ maxWidth: 1220, margin: "0 auto" }}>
      <PageHead
        title={<><span style={{ color: FK.text }}>{isEditing ? "Editar" : "Nuevo"}</span> <span style={{ color: FK.violet }}>plan</span></>}
        sub={isEditing ? (formData.nombre || "Sin nombre") : "Crea un nuevo plan con precios y descuentos"}
        onBack={() => navigate('/product_config/planes')}
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6 items-start">
          {/* ── Rail de secciones ── */}
          <aside className="lg:sticky lg:top-5">
            <div style={{ background: "#FBFBFC", border: "1px solid " + FK.border, borderRadius: 16, padding: 10 }}>
              <SideNav value={activeTab} options={railOptions} onChange={setActiveTab} />
            </div>
          </aside>

          {/* ── Panel de detalle ── */}
          <FormCard pad={0}>
            <div style={{ padding: "26px 28px 8px" }}>
              {meta && (
                <div style={{ marginBottom: 22 }}>
                  <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: "-0.01em", color: FK.text }}>{meta.title}</h2>
                  <p style={{ margin: "4px 0 0", fontSize: 13.5, color: FK.muted }}>{meta.desc}</p>
                </div>
              )}

              {activeTab === "informacion-basica" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  {/* Identificación */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    <Row>
                      <Field label="Nombre del plan" required htmlFor="nombre">
                        <TextInput id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} placeholder="Ej. Plan Complementario Cobertura 50%" />
                      </Field>
                      <Field label="Código del plan" htmlFor="codigo_plan">
                        <TextInput id="codigo_plan" name="codigo_plan" value={formData.codigo_plan} onChange={handleChange} upper placeholder="C20050" />
                      </Field>
                    </Row>
                    <Field label="Producto" required htmlFor="producto_id" full>
                      <Select
                        id="producto_id"
                        value={formData.producto_id || ""}
                        placeholder="Seleccione un producto"
                        options={productoOptions}
                        onChange={(e) => handleSelectChange('producto_id', e.target.value)}
                      />
                    </Field>
                    <Field label="Descripción" htmlFor="descripcion" full>
                      <TextArea id="descripcion" name="descripcion" value={formData.descripcion} onChange={handleChange} rows={3} placeholder="Describe el alcance y el capital del plan." />
                    </Field>
                    <Field label="URL de imagen del plan" htmlFor="image_url" full>
                      <TextInput id="image_url" name="image_url" type="url" value={formData.image_url} onChange={handleChange} placeholder="https://..." />
                      {formData.image_url && (
                        <img
                          src={formData.image_url}
                          alt="Vista previa"
                          className="mt-2 h-24 w-auto rounded-md border object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </Field>
                  </div>

                  {/* Documentos obligatorios */}
                  <Section
                    title="Documentos obligatorios"
                    desc="Condicionado, cuadro de coberturas y certificado condicionado en PDF."
                    right={<DocsMeter done={docsDone} total={3} />}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <FileUpload
                        label="Condicionado (PDF)"
                        value={formData.condicionado_archivo_id}
                        onChange={(archivoId) => handleSelectChange('condicionado_archivo_id', archivoId)}
                        accept=".pdf"
                      />
                      <FileUpload
                        label="Cuadro de Coberturas (PDF)"
                        value={formData.cuadro_coberturas_archivo_id}
                        onChange={(archivoId) => handleSelectChange('cuadro_coberturas_archivo_id', archivoId)}
                        accept=".pdf"
                      />
                      <FileUpload
                        label="Cert Condicionado (PDF)"
                        value={formData.certcondicionado_archivo_id}
                        onChange={(archivoId) => handleSelectChange('certcondicionado_archivo_id', archivoId)}
                        accept=".pdf"
                      />
                    </div>
                  </Section>

                  {/* Tipo de plan */}
                  <Section title="Tipo de plan">
                    <FkRadioGroup
                      value={formData.es_complementario ? "complementario" : "principal"}
                      onChange={(v) => {
                        if (v === "principal") {
                          setFormData(prev => ({ ...prev, es_complementario: false, planes_base: [] }));
                        } else {
                          setFormData(prev => ({ ...prev, es_complementario: true }));
                        }
                      }}
                      options={[
                        { value: "principal", label: "Plan principal" },
                        { value: "complementario", label: "Plan complementario" },
                      ]}
                    />

                    {formData.es_complementario && (
                      <div style={{ marginTop: 16, padding: "16px 18px", borderRadius: 12, border: "1px solid " + FK.border, background: "#FBFBFB" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: FK.text }}>¿A qué planes base se agrega este plan?</div>
                        <p style={{ margin: "3px 0 12px", fontSize: 12.5, color: FK.muted }}>Selecciona cuáles planes complementa.</p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
                          {planes
                            .filter(plan => plan.id !== id)
                            .map((plan) => {
                              const checked = formData.planes_base?.includes(plan.id) || false;
                              return (
                                <label
                                  key={plan.id}
                                  htmlFor={`plan-base-${plan.id}`}
                                  style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 10px", borderRadius: 10, cursor: "pointer", background: checked ? "#FFFFFF" : "transparent", border: "1px solid " + (checked ? FK.border : "transparent") }}
                                >
                                  <input
                                    type="checkbox"
                                    id={`plan-base-${plan.id}`}
                                    checked={checked}
                                    onChange={(e) => {
                                      setFormData(prev => ({
                                        ...prev,
                                        planes_base: e.target.checked
                                          ? [...(prev.planes_base || []), plan.id]
                                          : (prev.planes_base || []).filter(pid => pid !== plan.id)
                                      }));
                                    }}
                                    style={{ width: 16, height: 16, accentColor: FK.violet, cursor: "pointer", flexShrink: 0 }}
                                  />
                                  <span style={{ minWidth: 0, flex: 1 }}>
                                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 500, color: FK.text }}>{plan.nombre}</span>
                                    {plan.descripcion && <span style={{ display: "block", fontSize: 12, color: FK.muted }}>{plan.descripcion}</span>}
                                  </span>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </Section>

                  {/* Tipo de emisión */}
                  <Section
                    title="Tipo de emisión"
                    desc="Colectiva: puede vincularse a una póliza colectiva. Individual: se emite por asegurado."
                  >
                    <FkRadioGroup
                      value={formData.tipo_emision}
                      onChange={(v) => setFormData(prev => ({ ...prev, tipo_emision: v }))}
                      options={[
                        { value: "individual", label: "Individual" },
                        { value: "colectiva", label: "Colectiva" },
                      ]}
                    />
                  </Section>

                  {/* Plan exclusivo de empresa */}
                  <InfoToggleRow
                    titulo="Plan exclusivo de empresa"
                    on={formData.solo_empresa}
                    onChange={(v) => setFormData(prev => ({ ...prev, solo_empresa: v }))}
                  >
                    Si está activo, el plan no aparece en el cotizador público. Solo visible en{" "}
                    <code style={{ fontSize: 12, background: "#F1F3F4", borderRadius: 4, padding: "1px 5px", color: FK.sub }}>/empresa/[slug]</code>{" "}
                    para empleados autorizados.
                  </InfoToggleRow>

                  {/* Plan tipo URL: no se cotiza acá, se redirige a la compañía */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <InfoToggleRow
                      titulo="Plan tipo URL"
                      on={formData.is_url}
                      onChange={(v) => setFormData(prev => ({ ...prev, is_url: v, url: v ? prev.url : "" }))}
                    >
                      Si está activo, el plan no se cotiza en Yool: la card lleva al sitio de la
                      compañía aseguradora. El producto se comporta como tipo URL solo cuando{" "}
                      <strong>todos</strong> sus planes activos lo están.
                    </InfoToggleRow>

                    {formData.is_url && (
                      <Field
                        label="URL de destino"
                        required
                        htmlFor="url"
                        hint="Tiene que empezar con https://. Sin una URL válida el plan no aparece en el cotizador."
                        full
                      >
                        <TextInput
                          id="url"
                          name="url"
                          type="url"
                          value={formData.url}
                          onChange={handleChange}
                          placeholder="https://compania.cl/cotiza"
                        />
                      </Field>
                    )}
                  </div>
                </div>
              ) : activeTab === "configuracion-financiera" ? (
                <ConfiguracionFinancieraTab
                  formData={formData}
                  handleChange={handleChange}
                  handleSelectChange={handleSelectChange}
                />
              ) : activeTab === "configuracion-json" ? (
                <ConfiguracionJsonTab
                  jsonConfig={jsonConfig}
                  handleJsonConfigChange={handleJsonConfigChange}
                  formData={formData}
                  handleChange={handleChange}
                />
              ) : activeTab === "configuracion-plan" ? (
                <ConfiguracionPlanTab
                  formData={formData}
                  handleChange={handleChange}
                  planId={id}
                  recordId={id}
                  onSave={() => {
                    toast.success("Configuración guardada correctamente");
                  }}
                />
              ) : activeTab === "calculator-config" ? (
                <CalculatorConfigTab
                  jsonConfig={jsonConfig}
                  handleJsonConfigChange={handleJsonConfigChange}
                  planId={id}
                  productoId={formData.producto_id}
                  preciosBase={preciosBase}
                  hasChanges={hasChanges}
                />
              ) : activeTab === "sugerencias" ? (
                <SugerenciasTab
                  jsonConfig={jsonConfig}
                  handleJsonConfigChange={handleJsonConfigChange}
                />
              ) : activeTab === "precios-base" ? (
                <PreciosBaseTab
                  preciosBase={preciosBase}
                  setPreciosBase={setPreciosBase}
                  preciosCarga={preciosCarga}
                  setPreciosCarga={setPreciosCarga}
                  newPrecio={newPrecio}
                  setNewPrecio={setNewPrecio}
                  showPreciosForm={showPreciosForm}
                  setShowPreciosForm={setShowPreciosForm}
                  handleAddPrecio={handleAddPrecio}
                  handleRemovePrecio={handleRemovePrecio}
                  polizas={polizas}
                  planId={id}
                  cargasPricingMode={jsonConfig?.calculatorConfig?.cargasPricing?.mode}
                  descuentoAplicacion={jsonConfig?.calculatorConfig?.descuentosCargas?.aplicacion}
                  onDescuentoAplicacionChange={handleDescuentoAplicacionChange}
                  calculatorType={jsonConfig?.calculatorConfig?.type}
                />
              ) : activeTab === "descuentos-base" ? (
                <DescuentosBaseTab
                  descuentosBase={descuentosBase}
                  setDescuentosBase={setDescuentosBase}
                  newDescuento={newDescuento}
                  setNewDescuento={setNewDescuento}
                  showDescuentosForm={showDescuentosForm}
                  setShowDescuentosForm={setShowDescuentosForm}
                  handleAddDescuento={handleAddDescuento}
                  handleRemoveDescuento={handleRemoveDescuento}
                />
              ) : activeTab === "coberturas" ? (
                <CoberturasTab
                  formData={formData}
                  handleChange={handleChange}
                />
              ) : activeTab === "wallet-reverso" ? (
                // Guarda directo contra `planes.wallet_back_config` (no pasa por
                // el submit del formulario) porque al guardar hay que REGENERAR
                // los pases ya emitidos, y eso lo hace el endpoint.
                <BackConfigEditor planId={id} />
              ) : activeTab === "json-raw" ? (
                <JsonRawTab
                  formData={formData}
                  handleChange={handleChange}
                />
              ) : null}
            </div>

            {/* Acciones (footer sticky) */}
            <div style={{ position: "sticky", bottom: 0, padding: "14px 28px", background: "rgba(255,255,255,.92)", backdropFilter: "blur(8px)", borderTop: "1px solid " + FK.line, borderRadius: "0 0 16px 16px" }}>
              <ActionBar
                left={isEditing ? (
                  <FkDanger onClick={handleDelete} disabled={saving}><YIcon name="close" size={16} /> Eliminar</FkDanger>
                ) : null}
                right={
                  <FkPrimary type="submit" disabled={saving || !hasChanges}>
                    <YIcon name="check" size={16} /> {saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
                  </FkPrimary>
                }
              />
            </div>
          </FormCard>
        </div>
      </form>
    </div>
  );
}
